import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getInitialAuthState, loginRequest, logoutRequest } from "../services/authApi";
import { getApiErrorMessage } from "../utils/helpers";
import {
  api,
  AUTH_USER_STORAGE_KEY,
  getStoredAuthUser,
  getStoredTokens,
  storeAuthUser,
  subscribeToAuthSessionExpired,
} from "../services/apiClient";
import { API_PATHS } from "../config/api";
import { isJwtExpired } from "../utils/jwt";

const AuthContext = createContext();
const SESSION_CHECK_INTERVAL_MS = 30_000;

const normalizeUser = (me) => {
  if (!me) return null;
  const role = me?.role ? String(me.role).toUpperCase() : me?.role;
  const isAdmin = role === "ADMIN";
  const canCompleteCS = isAdmin || me?.can_complete_cs_effective === true;
  return { ...me, role, isAdmin, canCompleteCS };
};

export const AuthProvider = ({ children }) => {
  const initial = useMemo(() => getInitialAuthState(), []);

  const [isAuthenticated, setIsAuthenticated] = useState(initial.isAuthenticated);
  const [user, setUser] = useState(() => normalizeUser(initial.user));
  const [authLoading, setAuthLoading] = useState(false);
  const [authRevalidating, setAuthRevalidating] = useState(initial.hasStoredSession);
  const authRequestSeqRef = useRef(0);
  const sessionValidationRef = useRef(null);

  // boot/login 요청이 겹칠 때 오래된 요청이 최신 로그인 상태를 덮지 못하도록 한다.
  const startAuthRequest = useCallback(() => {
    authRequestSeqRef.current += 1;
    return authRequestSeqRef.current;
  }, []);

  const isCurrentAuthRequest = useCallback(
    (seq) => authRequestSeqRef.current === seq,
    []
  );

  const endLocalSession = useCallback(() => {
    authRequestSeqRef.current += 1;
    setUser(null);
    setIsAuthenticated(false);
    setAuthLoading(false);
    setAuthRevalidating(false);
  }, []);

  const acceptAuthenticatedUser = useCallback((account) => {
    const normalized = normalizeUser(account);
    if (!normalized) throw new Error("Authenticated user payload is missing.");

    storeAuthUser(normalized);
    setUser(normalized);
    setIsAuthenticated(true);
    return normalized;
  }, []);

  // 같은 탭에서 refresh가 최종 실패하면 즉시 보호 라우트를 닫는다.
  useEffect(() => subscribeToAuthSessionExpired(endLocalSession), [endLocalSession]);

  // 다른 탭에서 로그아웃하거나 저장소를 비운 경우 현재 탭도 함께 종료한다.
  useEffect(() => {
    const handleStorage = (event) => {
      const userWasRemoved =
        (event.key === AUTH_USER_STORAGE_KEY || event.key === null) &&
        !getStoredAuthUser();

      if (userWasRemoved) endLocalSession();
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [endLocalSession]);

  // 저장된 세션 표식이 있으면 /me 검증을 마친 뒤 보호 화면을 연다.
  useEffect(() => {
    let mounted = true;

    const boot = async () => {
      if (!initial.hasStoredSession) return;

      const reqSeq = startAuthRequest();
      setAuthRevalidating(true);

      try {
        const meRes = await api.get(API_PATHS.me);
        if (!mounted || !isCurrentAuthRequest(reqSeq)) return;

        acceptAuthenticatedUser(meRes.data);
      } catch (error) {
        if (!mounted || !isCurrentAuthRequest(reqSeq)) return;

        const isAuthenticationFailure =
          error?.code === "AUTH_SESSION_EXPIRED" || error?.response?.status === 401;

        // 인증이 실제 거절된 경우만 저장 세션까지 폐기한다. 일시 오류는 다음 접속 때 재시도한다.
        if (isAuthenticationFailure) {
          void logoutRequest();
          endLocalSession();
        }
      } finally {
        if (mounted && isCurrentAuthRequest(reqSeq)) setAuthRevalidating(false);
      }
    };

    boot();
    return () => {
      mounted = false;
    };
  }, [
    acceptAuthenticatedUser,
    endLocalSession,
    initial.hasStoredSession,
    isCurrentAuthRequest,
    startAuthRequest,
  ]);

  // 유휴 탭도 만료된 access를 방치하지 않도록 주기 및 탭 복귀 시 refresh를 확인한다.
  useEffect(() => {
    if (!isAuthenticated || authRevalidating) return undefined;

    const validateExpiredAccess = () => {
      const { access } = getStoredTokens();
      if (!access) {
        endLocalSession();
        return;
      }
      if (!isJwtExpired(access) || sessionValidationRef.current) return;

      const request = api
        .get(API_PATHS.me)
        .then((response) => {
          acceptAuthenticatedUser(response.data);
        })
        .catch(() => {
          // 인증 실패는 apiClient가 전역으로 알린다. 일시적인 통신 실패는 세션을 유지한다.
        })
        .finally(() => {
          if (sessionValidationRef.current === request) {
            sessionValidationRef.current = null;
          }
        });

      sessionValidationRef.current = request;
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") validateExpiredAccess();
    };

    const intervalId = window.setInterval(validateExpiredAccess, SESSION_CHECK_INTERVAL_MS);
    window.addEventListener("focus", validateExpiredAccess);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    validateExpiredAccess();

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", validateExpiredAccess);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [acceptAuthenticatedUser, authRevalidating, endLocalSession, isAuthenticated]);

  // 로그인 응답의 사용자 정보로 즉시 권한을 확정하고, 구버전 API는 /me로 보완한다.
  const login = async ({ username, password }) => {
    const reqSeq = startAuthRequest();
    setAuthLoading(true);
    try {
      const loginData = await loginRequest({ username, password });
      const account = loginData?.user || (await api.get(API_PATHS.me)).data;
      if (!isCurrentAuthRequest(reqSeq)) {
        return { ok: false, message: "로그인 요청이 갱신되었습니다. 다시 시도해 주세요." };
      }

      acceptAuthenticatedUser(account);
      return { ok: true };
    } catch (error) {
      if (isCurrentAuthRequest(reqSeq)) {
        void logoutRequest();
        endLocalSession();
      }
      return {
        ok: false,
        message: getApiErrorMessage(error, error?.message || "Login failed"),
      };
    } finally {
      if (isCurrentAuthRequest(reqSeq)) setAuthLoading(false);
    }
  };

  const logout = () => {
    void logoutRequest();
    endLocalSession();
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, user, authLoading, authRevalidating, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
